import React, { useEffect, useState } from 'react';
import { FaRegQuestionCircle, FaCheckCircle } from 'react-icons/fa';
import Logo from '../Images/logo.png';
import { HiMenu, HiX } from 'react-icons/hi';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import HeroSection from './HeroSection';
import ComparisonTable from './ComparisonTable';
import PricingSection from './Pricing';
import ArrowSeparator from './ArrowSeparator';
import WhyUseTrendhopper from './WhyUseUs';
import Integrations from './Integrations';
import BlogPreview from "./BlogPreview";
import { motion } from "framer-motion";
import IntegrationOverview from './IntegrationOverview'
import ProcessSteps from './ProcessSteps';
import MarketingVideo from './MarketingVideo';
import { IoArrowForwardOutline } from "react-icons/io5";
import FaqSection from './FAQ'
import CTA from './CTA';
import { Helmet } from 'react-helmet';
import ContactModal from './ContactModal';
const LandingPage = () => {
    const [redditUrl, setRedditUrl] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const [showContactModal, setShowContactModal] = useState(false);
    const homePageSubmittedURL = 'homePageSubmittedURL';
    const openContactModal = () => {
        setShowContactModal(true);
      };
    
      const closeContactModal = () => {
        setShowContactModal(false);
      };
    const fadeInVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
    };

    useEffect(() => {
        // Clear the specific URL from the browser cache on load
        caches.keys().then(function (names) {
            for (let name of names) {
                caches.delete(name);
            }
        });

        localStorage.removeItem(homePageSubmittedURL);


        if (!isLoading && isAuthenticated) {
            navigate('/dashboard');
        }

    }, [isAuthenticated, navigate, isLoading]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <ClipLoader color="#ff6347" loading={isLoading} size={50} />
            </div>
        );
    }

    const faqItems = [
        {
            question: 'How does Trendhopper automate blog creation?',
            answer: 'Trendhopper uses AI to automatically generate one high-quality blog post per day tailored to your business niche. It handles everything from keyword research and content structuring to formatting and adding relevant photos with captions.'
        },
        {
            question: 'Can I approve or reject blog ideas before they’re created?',
            answer: 'Yes, Trendhopper allows you to review and approve or reject blog ideas before they are written. This ensures the content aligns with your business goals and target audience.'
        },
        {
            question: 'Can I provide feedback on drafts before publishing?',
            answer: 'Absolutely! Trendhopper gives you the ability to review drafts and provide feedback. The AI refines the content based on your input to ensure it meets your expectations before publishing.'
        },
        {
            question: 'Can Trendhopper post blogs on my behalf?',
            answer: 'Yes, Trendhopper integrates seamlessly with popular blogging platforms like WordPress, Ghost, and more. Once a blog is finalized, it can be scheduled or directly published, saving you time and effort.'
        },
        {
            question: 'How does Trendhopper handle keyword research?',
            answer: 'Trendhopper automates the keyword research process by analyzing your niche, target audience, and search trends. Each blog post is crafted to rank for relevant keywords, ensuring maximum visibility and organic traffic.'
        },
        {
            question: 'Why is Trendhopper better than other blogging platforms?',
            answer: 'Unlike other platforms, Trendhopper fully automates the blog creation process, including keyword research, photo generation, captions, and publishing. Additionally, it allows you to approve ideas, give feedback on drafts, and ensures that every blog post aligns with your vision and brand.'
        },
        {
            question: 'How does Trendhopper ensure the quality of blog posts?',
            answer: 'Trendhopper’s AI analyzes industry trends, writing styles, and SEO best practices to produce professional, engaging, and relevant content. Each blog is tailored to your business needs, complete with high-quality visuals and captions.'
        }
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        localStorage.setItem(homePageSubmittedURL, redditUrl);
        loginWithRedirect();
    };

    const handleStartForFreeClick = () => {
        loginWithRedirect();
    };

    const handleContactUsClick = () => {
        window.location.href = 'mailto:matt@trendhopper.ai?subject=Contact Us - TrendHopper';
    };

    const handleFaqClick = (e) => {
        e.preventDefault(); // Prevent default anchor behavior
        const element = document.getElementById('faq');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            // Close the mobile menu if it's open
            if (isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        }
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <Helmet>
                <link rel="canonical" href="https://www.trendhopper.ai" />
            </Helmet>
            <style>
                {`.full-viewport-section { min-height: 100vh; display: flex; flex-direction: column; }`}
            </style>
            <div className="full-viewport-section">
                <header className="absolute top-0 left-0 w-full z-50 bg-transparent">
                    <div className="container mx-auto px-4 h-16 grid grid-cols-3 items-center">
                        {/* Left-aligned Logo */}
                        <div className="flex items-center justify-self-start">
                            <img src={Logo} alt="Trendhopper" className="w-10 h-10" />
                            <span className="font-semibold text-xl text-white">Trendhopper</span>
                        </div>

                        {/* Centered Navigation */}
                        <nav className="hidden md:flex items-center justify-center">
                            <ul className="flex gap-6">
                                <li><a href="#integrations" className="text-white hover:text-gray-400">How it Works</a></li>
                                <li><a href="#examples" className="text-white hover:text-gray-400">Writing Examples</a></li>
                                <li><a href="#pricing" className="text-white hover:text-gray-400">Pricing</a></li>
                            </ul>
                        </nav>

                        {/* Right-aligned Auth Buttons */}
                        <div className="hidden md:flex items-center justify-self-end">
                            <button
                                onClick={loginWithRedirect}
                                type="button"
                                className="text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
                            >
                                Login
                            </button>
                            <button
                                onClick={handleStartForFreeClick}
                                type="button"
                                className="text-white flex items-center bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-6 py-2 min-w-[120px] me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
                            >
                                Sign Up
                                <IoArrowForwardOutline className='ml-1' size={16} />
                            </button>
                        </div>

                        {/* Mobile Menu Button (moved outside the grid for full-width) */}
                        <div className="md:hidden absolute top-4 right-4">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="text-white focus:outline-none"
                            >
                                {isMobileMenuOpen ? <HiX size={28} /> : <HiMenu size={28} />}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Dropdown Menu */}
                    {isMobileMenuOpen && (
                        <div className="absolute top-16 left-0 w-full bg-slate-900 shadow-lg flex flex-col items-center py-4 space-y-4 md:hidden">
                            <button
                                onClick={() => document.getElementById('integrations').scrollIntoView({ behavior: 'smooth' })}
                                className="text-slate-300 hover:text-white">
                                How It Works
                            </button>
                            <button onClick={() => document.getElementById('examples').scrollIntoView({ behavior: 'smooth' })} className="text-slate-300 hover:text-white">
                                Writing Examples
                            </button>
                            <button
                                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                                className="text-slate-300 hover:text-white">
                                Pricing
                            </button>
                            <button

                                className="text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"

                                onClick={loginWithRedirect}>
                                Log in
                            </button>
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
                                onClick={handleStartForFreeClick}
                            >
                                Start for free
                                <IoArrowForwardOutline size={18} />
                            </button>
                        </div>
                    )}
                </header>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInVariants}
                >
                    <section className="relative min-h-[95vh] flex flex-col items-center justify-center text-center">
                        {/* Background Grid with Fading Lines */}
                        <div
                            className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"
                            style={{
                                WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0))",
                                maskImage: "linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0))"
                            }}
                        ></div>

                        <div className="relative z-10 container mx-auto px-4 lg:px-8">
                            <div className="inline-flex items-center bg-[#1A1D26] text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg mb-6">
                                <span className="mr-2">🎉</span>
                                <span>Limited 50% off monthly plan</span>
                            </div>

                            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="block">Automate</span>
                                <span className="inline-block bg-gradient-to-r from-orange-500 to-rose-500 text-white px-3 py-1 rounded-md">
                                    SEO Blog Content
                                </span>
                                <span className="block bg-gradient-to-r from-orange-400 to-rose-400 bg-clip-text text-transparent">
                                    that drives traffic 24/7
                                </span>
                            </h1>

                            <p className="mx-auto mb-3 max-w-3xl text-slate-400">
                                Save time and grow your organic traffic with AI-powered content generation.
                            </p>

                            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto relative">
                                <div className="flex items-center bg-white rounded-full overflow-hidden shadow-md w-full max-w-md mx-auto">
                                    <input
                                        type="text"
                                        placeholder="Enter your domain"
                                        className="flex-1 px-3 py-2 text-slate-700 placeholder-slate-400 border-none focus:outline-none focus:ring-0 text-sm"
                                        value={redditUrl}
                                        onChange={(e) => setRedditUrl(e.target.value)}
                                    />
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-medium text-sm md:text-base rounded-r-full focus:outline-none hover:from-orange-600 hover:to-rose-600 shadow-md transition-transform transform hover:scale-105"
                                    >
                                        Start Automating
                                    </button>
                                </div>

                                <div className="flex justify-center items-center space-x-4 mt-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="text-green-500">
                                            <FaCheckCircle />
                                        </span>
                                        <span className="text-white">Quick setup</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-green-500">
                                            <FaCheckCircle />
                                        </span>
                                        <span className="text-white">7-day free trial</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-green-500">
                                            <FaCheckCircle />
                                        </span>
                                        <span className="text-white">No credit card required</span>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </section>


                </motion.div>

                <MarketingVideo />

                {/* <Integrations /> */}
                <ArrowSeparator direction="down" />

            </div>
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <HeroSection />
            </motion.div>
            <ArrowSeparator direction="down" />
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <ComparisonTable />

                <ArrowSeparator direction="down" />

            </motion.div>

            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <PricingSection id="pricing" />
            </motion.div>
            <ArrowSeparator direction="down" />


            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <FaqSection id="faq" />
            </motion.div>
            <ArrowSeparator direction="down" />

            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <ProcessSteps />
            </motion.div>

            <ArrowSeparator direction="down" />
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <WhyUseTrendhopper />
            </motion.div>
            <ArrowSeparator direction="down" />
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <BlogPreview />
            </motion.div>
            <ArrowSeparator direction="down" />

            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInVariants}
            >
                <IntegrationOverview />
            </motion.div>
            <ArrowSeparator direction="down" />

            <CTA />

            {/* Centered and contained gradient line */}
            <div className="w-full flex justify-center">
                <div className="h-[2px] w-3/4 md:w-2/3 lg:w-1/2 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
            </div>

            <footer className="mt-12 bg-slate-900 text-white">
    <div className="container mx-auto px-6 lg:max-w-screen-xl xl:max-w-screen-lg py-10">
        {/* Footer Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center md:text-left">
            {/* Left: Brand Info */}
            <div>
                <div className="flex justify-center md:justify-start items-center space-x-2 mb-4">
                    <img
                        src={Logo}
                        alt="Trendhopper"
                        className="w-8 h-8 transition-transform hover:scale-105"
                    />
                    <span className="font-semibold text-xl">Trendhopper</span>
                </div>
                <p className="text-sm text-slate-300">
                    Automating content with AI.
                </p>
            </div>

            {/* Right: Links and Contact */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ul className="space-y-2">
                    <li>
                        <a href="/blog" className="hover:text-blue-500 transition-colors" title="Visit our blog">
                            Blog
                        </a>
                    </li>
                    <li>
                        <a href="#how-it-works" className="hover:text-blue-500 transition-colors" title="How it Works">
                            How it Works
                        </a>
                    </li>
                </ul>
                <ul className="space-y-2">
                    <li>
                        <a href="#pricing" className="hover:text-blue-500 transition-colors" title="Pricing">
                            Pricing
                        </a>
                    </li>
                    <li>
                        <a href="#" onClick={handleFaqClick} className="hover:text-blue-500 transition-colors" title="FAQ">
                            FAQ
                        </a>
                    </li>
                </ul>
                {/* Contact Us - Adjusted Placement */}
                <div className="space-y-2">
                    <button 
                        onClick={() => setShowContactModal(true)}  // Trigger modal visibility
                        className="text-white hover:text-blue-500 transition-colors">
                        Contact Us
                    </button>
                </div>
            </div>
        </div>

        {/* Centered Copyright Text */}
        <div className="pt-8 flex justify-center text-center">
            <p className="text-sm text-slate-400">
                © {new Date().getFullYear()} Trendhopper. All rights reserved.
            </p>
        </div>
    </div>

    {/* Add the modal here */}
    {showContactModal && <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />}
</footer>



        </div>
    );
};

export default LandingPage;
